import useFindInvoicesQuery from '@/api/queries/useFindInvoicesQuery';
import Button from '@/components/Button';
import SpinnerIcon from '@/components/icons/SpinnerIcon';
import PageHeader from '@/components/PageHeader';
import BillingActionButtons from '@/modules/admin/components/BillingActionButtons';
import Breadcrumbs from '@/modules/admin/components/Breadcrumbs';
import {
  AdminBillingFailedTransactionsPath,
  AdminBillingInvoiceListPath,
  AdminBillingPricesPath,
} from '@/modules/admin/paths';

const BillingHome = () => {
  const { invoices, isLoading, error } = useFindInvoicesQuery(1);

  return (
    <>
      <Breadcrumbs />

      <PageHeader title="Billing">
        <Button to={AdminBillingInvoiceListPath} variant="secondary">
          View Invoices
        </Button>
        <Button to={AdminBillingPricesPath} variant="secondary">
          View Prices
        </Button>
        <BillingActionButtons />
      </PageHeader>

      {isLoading ? (
        <div className="flex justify-center py-20">
          <SpinnerIcon />
        </div>
      ) : error ? (
        <div className="rounded-md bg-red-50 p-4 text-sm text-red-700">
          Failed to load billing summary.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <div className="text-sm font-medium text-slate-500">Total invoices</div>
            <div className="mt-2 text-3xl font-semibold text-slate-900">
              {(invoices.totalCount ?? invoices.estimatedTotalCount).toLocaleString()}
            </div>
            <div className="mt-2 text-sm text-slate-600">
              Current invoice records available in the billing module.
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6 lg:col-span-2">
            <div className="text-sm font-medium text-slate-500">Operational actions</div>
            <div className="mt-2 text-sm text-slate-700">
              Use the actions above to generate invoices and process pending invoice transactions.
            </div>
            <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="rounded-xl bg-slate-50 p-4">
                <div className="text-sm font-medium text-slate-900">Generate invoices</div>
                <div className="mt-1 text-sm text-slate-600">
                  Create invoices for subscriptions due in the current billing cycle and create
                  their transaction records immediately.
                </div>
              </div>
              <div className="rounded-xl bg-slate-50 p-4">
                <div className="text-sm font-medium text-slate-900">
                  Process invoice transactions
                </div>
                <div className="mt-1 text-sm text-slate-600">
                  Run payment processing for pending invoice transactions.
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6 lg:col-span-3">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-sm font-medium text-slate-500">Failed transactions</div>
                <div className="mt-2 text-sm text-slate-700">
                  Review failed payment attempts and retry them individually.
                </div>
              </div>

              <Button to={AdminBillingFailedTransactionsPath} variant="secondary">
                View failed transactions
              </Button>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6 lg:col-span-3">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-sm font-medium text-slate-500">Billing prices</div>
                <div className="mt-2 text-sm text-slate-700">
                  Review the configured pricing by owner type, country, currency, and product.
                </div>
              </div>

              <Button to={AdminBillingPricesPath} variant="secondary">
                View prices
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BillingHome;
